import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * 사이드바 즐겨찾기 — <b>계정별</b>로 담고, 그 안에서 차례를 매긴다 (V325 · 사용자 지시 2026-09-22).
 *
 * <h2>브라우저에 두지 않는다</h2>
 * 사무실 PC 와 현장 패드에서 같은 줄이 보여야 하고, 캐시를 비웠다고 사라지면 안 된다 — 현장
 * 즐겨찾기(V320)·홈 위젯 배치와 같은 판단이다.
 *
 * <h2>담는 것은 «메뉴 키» 뿐이다</h2>
 * 이름도 주소도 담지 않는다. 둘 다 바뀌는 값이고(관리자가 라벨을 고치고, 화면이 옮겨 간다),
 * 담아 두면 사이드바에는 옛 이름이 남는다. 그리는 쪽이 키로 지금 트리에서 찾아 쓴다 —
 * <b>트리에 없으면 그리지 않는다</b>. 메뉴가 없어지거나(지금 전자결재를 빼는 중이다) 권한이
 * 내려가면 그 줄은 조용히 사라진다.
 */
export const menuFavoritesQueryKey = ['menu-favorites'] as const;

export function useMenuFavorites() {
  return useQuery({
    queryKey: menuFavoritesQueryKey,
    queryFn: () => apiFetch<string[]>('/v1/menu-favorites'),
    // 사이드바는 모든 화면에 있다. 화면을 옮길 때마다 다시 묻지 않게 넉넉히 둔다.
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useMenuFavoriteActions() {
  const queryClient = useQueryClient();
  /** 응답이 곧 «바뀐 뒤의 전체 목록» 이라 다시 묻지 않고 그대로 캐시에 앉힌다. */
  const put = (next: string[]) => queryClient.setQueryData(menuFavoritesQueryKey, next);

  const toggle = useMutation({
    mutationFn: ({ menuKey, favorite }: { menuKey: string; favorite: boolean }) =>
      apiFetch<string[]>(`/v1/menu-favorites/${menuKey}`, { method: 'PUT', body: { favorite } }),
    onSuccess: put,
  });

  const reorder = useMutation({
    mutationFn: (menuKeys: string[]) =>
      apiFetch<string[]>('/v1/menu-favorites/order', { method: 'PUT', body: { menuKeys } }),
    /*
      순서는 <b>먼저 바꿔 놓고</b> 보낸다. ↑ 를 눌렀는데 한 박자 뒤에 줄이 움직이면 사람은 한 번 더
      누르고, 그 두 번째 누름이 첫 결과를 덮어 제자리로 돌아간다.
    */
    onMutate: (menuKeys) => put(menuKeys),
    onSuccess: put,
    // 실패하면 서버 것으로 되돌린다 — 화면만 바뀐 채로 남는 것이 가장 나쁘다.
    onError: () => void queryClient.invalidateQueries({ queryKey: menuFavoritesQueryKey }),
  });

  return { toggle, reorder };
}
